import type { DailyReportRow } from '@/types';

/**
 * CSV export of the daily report exactly as queried — raw values, not the
 * formatted ones on screen. Amounts keep their full precision (they are strings
 * end to end), because a finance export that silently rounds is worse than none.
 *
 * A `total` row is deliberately NOT appended: `players` is a per-day distinct
 * count, so a column sum would overstate the period's players, and a
 * spreadsheet's own SUM() over these rows is the reader's to write knowingly.
 */
export function toCsv(rows: DailyReportRow[]): string {
  const header = [
    'date_utc',
    'bet',
    'win',
    'rollback',
    'turnover',
    'ggr',
    'hold',
    'players',
    'bets',
    'rounds',
  ];
  const lines = rows.map((r) =>
    [
      r.day,
      r.bet,
      r.win,
      r.rollback_amount,
      r.turnover,
      r.ggr,
      String(r.hold),
      String(r.players),
      String(r.bets),
      String(r.rounds),
    ].map(escapeCell),
  );
  return [header.join(','), ...lines.map((l) => l.join(','))].join('\n');
}

/** RFC-4180 quoting: wrap when the value could break the row, double inner quotes. */
function escapeCell(value: string): string {
  const v = value ?? '';
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function downloadCsv(filename: string, csv: string): void {
  // BOM so Excel opens UTF-8 correctly.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Ticket-friendly filename built from the CLIENT NAME, not the play_service_id —
 * a 40-char opaque hash tells nobody which project the export is for. Sanitised
 * so a name with spaces or punctuation can't inject a path.
 */
export function financeFilename(client: string, currency: string, from: string, to: string): string {
  const safe = (s: string) => s.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 40);
  return `daily-finance_${safe(client)}_${safe(currency)}_${safe(from.slice(0, 10))}_${safe(
    to.slice(0, 10),
  )}.csv`;
}
